import React, { useState } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { ArrowLeftIcon, CheckIcon, PlayIcon } from 'lucide-react';
import { getProductBySlug } from '../data/products';
import { ProductSpecs } from '../components/products/ProductSpecs';
import { Button } from '../components/ui/Button';
import { Modal } from '../components/ui/Modal';
import { useTranslation } from '../lib/i18n';
interface ProductDetailPageProps {
  onRequestQuote: (productId: string) => void;
}
export function ProductDetailPage({
  onRequestQuote
}: ProductDetailPageProps) {
  const {
    lang
  } = useTranslation();
  const en = lang === 'en';
  const ru = lang === 'ru';
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const product = slug ? getProductBySlug(slug) : undefined;
  const [activeMedia, setActiveMedia] = useState<{
    type: 'image' | 'video';
    url: string;
  } | null>(null);
  if (!product) {
    return <Navigate to="/products" replace data-id="element-882" />;
  }
  const hasGallery = product.gallery && product.gallery.length > 0;
  return <main className="min-h-screen bg-dark-bg" data-id="element-883">
      {/* Back Link */}
      <div className="bg-dark-surface border-b-2 border-dark-border" data-id="element-884">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4" data-id="element-885">
          <Link to="/products" className="inline-flex items-center text-gray-400 hover:text-brand-yellow transition-colors focus:outline-none focus:ring-2 focus:ring-brand-yellow rounded-industrial" data-id="element-886">
            <ArrowLeftIcon className="w-4 h-4 mr-2" data-id="element-887" />
            {en ? 'Back to products' : ru ? 'Назад к продуктам' : 'Nazad na proizvode'}
          </Link>
        </div>
      </div>

      {/* Product Hero */}
      <section className="py-12 bg-dark-surface border-b-2 border-dark-border" data-id="element-888">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-889">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start" data-id="element-890">
            <div className="space-y-4" data-id="element-891">
              <div className="w-full bg-gradient-to-br from-gray-900 to-gray-800 rounded-industrial p-8 flex items-center justify-center min-h-[400px] lg:min-h-[500px] relative" data-id="element-892">
                <img src={product.image} alt={`${product.name} ${product.model}`} className="w-full h-auto object-contain max-h-[450px]" data-id="element-893" />
              </div>

              {/* Gallery Thumbnails */}
              {hasGallery && <div className="mt-6" data-id="element-894">
                  <h3 className="text-lg font-bold text-white mb-3" data-id="element-895">
                    {en ? 'Gallery' : ru ? 'Галерея' : 'Galerija'}
                  </h3>
                  <div className="grid grid-cols-4 gap-4" data-id="element-896">
                    {product.gallery?.map((media, idx) => <button key={idx} onClick={() => setActiveMedia(media)} className="relative aspect-square bg-dark-bg border-2 border-dark-border rounded-industrial overflow-hidden hover:border-brand-yellow transition-colors group" data-id="element-897">
                        {media.type === 'image' ? <img src={media.url} alt="Gallery item" className="w-full h-full object-cover opacity-70 group-hover:opacity-100 transition-opacity" data-id="element-898" /> : <div className="w-full h-full flex items-center justify-center bg-gray-900 group-hover:bg-gray-800 transition-colors" data-id="element-899">
                            <PlayIcon className="w-8 h-8 text-brand-yellow" data-id="element-900" />
                          </div>}
                      </button>)}
                  </div>
                </div>}
            </div>

            <div className="pt-4" data-id="element-901">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-black text-white mb-2" data-id="element-902">
                {product.name}
              </h1>
              <p className="text-2xl text-gray-400 mb-6" data-id="element-903">{product.model}</p>
              <p className="text-xl text-gray-300 mb-8 leading-relaxed" data-id="element-904">
                {en ? product.descriptionEn || product.description : ru ? product.descriptionRu || product.description : product.description}
              </p>
              <div className="mb-8" data-id="element-905">
                <div className="flex items-baseline gap-3" data-id="element-906">
                  <span className="text-5xl font-black text-brand-yellow" data-id="element-907">
                    €{product.price.toLocaleString()}
                  </span>
                  <span className="text-base text-gray-400" data-id="element-908">
                    {en ? 'excl. VAT' : ru ? 'без НДС' : 'bez PDV'}
                  </span>
                </div>
                <p className="text-sm text-gray-500 mt-1" data-id="element-909">
                  {en ? 'incl. VAT' : ru ? 'с НДС' : 'Sa PDV'}: €
                  {Math.round(product.price * 1.2).toLocaleString()} ·
                  {en ? ' Availability on request.' : ru ? ' Наличие по запросу.' : ' Dostupnost na upit.'}
                </p>
              </div>
              <Button variant="primary" size="lg" onClick={() => onRequestQuote(product.id)} data-id="element-910">
                {en ? 'Request a quote' : ru ? 'Запросить цену' : 'Zatražite ponudu'}
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Highlights */}
      <section className="py-12 bg-dark-bg" data-id="element-911">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-912">
          <h2 className="text-3xl font-black text-white mb-6" data-id="element-913">
            {en ? 'Key features' : ru ? 'Ключевые особенности' : 'Ključne karakteristike'}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-914">
            {(en && product.highlightsEn ? product.highlightsEn : ru && product.highlightsRu ? product.highlightsRu : product.highlights).map(highlight => <div key={highlight} className="flex items-center space-x-3 bg-dark-surface border-2 border-dark-border rounded-industrial p-4" data-id="element-915">
                <CheckIcon className="w-6 h-6 text-brand-yellow flex-shrink-0" data-id="element-916" />
                <span className="text-white font-semibold" data-id="element-917">{highlight}</span>
              </div>)}
          </div>
        </div>
      </section>

      {/* Technical Specifications */}
      <section className="py-12 bg-dark-surface" data-id="element-918">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-919">
          <h2 className="text-3xl font-black text-white mb-6" data-id="element-920">
            {en ? 'Technical specifications' : ru ? 'Технические характеристики' : 'Tehničke specifikacije'}
          </h2>
          <ProductSpecs specs={product.specs} data-id="element-921" />
        </div>
      </section>

      {/* Features */}
      <section className="py-12 bg-dark-bg" data-id="element-922">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-923">
          <h2 className="text-3xl font-black text-white mb-6" data-id="element-924">
            {en ? 'Features and benefits' : ru ? 'Функции и преимущества' : 'Funkcije i prednosti'}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-925">
            {(en && product.featuresEn ? product.featuresEn : ru && product.featuresRu ? product.featuresRu : product.features).map(feature => <div key={feature} className="flex items-start space-x-3 bg-dark-surface border-2 border-dark-border rounded-industrial p-6" data-id="element-926">
                <CheckIcon className="w-5 h-5 text-brand-yellow flex-shrink-0 mt-0.5" data-id="element-927" />
                <span className="text-gray-300" data-id="element-928">{feature}</span>
              </div>)}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-dark-surface border-t-2 border-dark-border" data-id="element-929">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-930">
          <h2 className="text-4xl font-black text-white mb-4" data-id="element-931">
            {en ? 'Ready to order?' : ru ? 'Готовы заказать?' : 'Spremni za poručivanje?'}
          </h2>
          <p className="text-xl text-gray-400 mb-8" data-id="element-932">
            {en ? 'Contact us for a detailed quote and lead time.' : ru ? 'Свяжитесь с нами для получения подробного предложения и сроков поставки.' : 'Kontaktirajte nas za detaljnu ponudu i rok isporuke.'}
          </p>
          <Button variant="primary" size="lg" onClick={() => onRequestQuote(product.id)} data-id="element-933">
            {en ? 'Request a quote' : ru ? 'Запросить цену' : 'Zatražite ponudu'}
          </Button>
        </div>
      </section>

      {/* Media Modal */}
      <Modal isOpen={!!activeMedia} onClose={() => setActiveMedia(null)} title={product.name} data-id="element-934">
        <div className="bg-dark-bg rounded-industrial overflow-hidden flex items-center justify-center min-h-[50vh]" data-id="element-935">
          {activeMedia?.type === 'image' ? <img src={activeMedia.url} alt="Gallery full" className="max-w-full max-h-[80vh] object-contain" data-id="element-936" /> : activeMedia?.type === 'video' ? <video src={activeMedia.url} controls autoPlay className="max-w-full max-h-[80vh]" data-id="element-937" /> : null}
        </div>
      </Modal>
    </main>;
}